import { useCallback, useMemo } from "react";

import {
  JsonParam,
  NumberParam,
  StringParam,
  createEnumParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  type FixedDateInterval,
} from "@north-beam/nb-common";
import { CommonTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useAdoptedMix } from "@pages/mmm/hooks/use-adopted-mix";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { BudgetMixCardMode } from "../components/explore-budgets/budget-mix-card/card";
import { type ModelMetadataKpiFormula } from "../components/explore-budgets/client-setting/types";
import {
  MODEL_FIT_ACTUAL_ID,
  MODEL_FIT_PREDICTION_HDI_ID,
  MODEL_FIT_PREDICTION_ID,
  MODEL_FIT_SEASONALITY_ID,
  MODEL_FIT_SPEND_ID,
} from "../components/model-insights/model-fit-chart/utils/chart-dataset-helpers";

import {
  getCacDataStateInitialValue,
  useCacDataState,
} from "./use-cac-data-state";
import {
  getInitialHiddenBudgetMixes,
  useHiddenBudgetMixes,
} from "./use-hidden-budget-mixes";
import { useMMMNotifications } from "./use-mmm-notifications";
import {
  getRoasDataStateInitialValue,
  useRoasDataState,
} from "./use-roas-data-state";

export enum ActiveTab {
  SUMMARY = "summary",
  EXPLORE_MIXES = "explore-mixes",
  MODEL_INSIGHTS = "model-insights",
  PERFORMANCE_TRACKING = "performance-tracking",
  MEDIA_PLAN = "media-plan",
}

export const ActiveTabOrdering = [
  ActiveTab.EXPLORE_MIXES,
  ActiveTab.MODEL_INSIGHTS,
  ActiveTab.PERFORMANCE_TRACKING,
  ActiveTab.SUMMARY,
];

export const PrettyActiveTab = {
  [ActiveTab.EXPLORE_MIXES]: "Explore Budgets",
  [ActiveTab.MODEL_INSIGHTS]: "View Incremental ROAS/CAC",

  [ActiveTab.PERFORMANCE_TRACKING]: "Track Performance",
  [ActiveTab.SUMMARY]: "Recap Performance",
  [ActiveTab.MEDIA_PLAN]: "Media Plans",
};

const ActiveTabParam = createEnumParam(Object.values(ActiveTab));
export const useMMMActiveTab = () => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.ALL_TABS);

  const [activeTab, _setActiveTab] = useQueryParam(
    "mmm-tab",
    withDefault(ActiveTabParam, null),
    { updateType: "pushIn" },
  );

  const setActiveTab: typeof _setActiveTab = useCallback(
    (...args) => {
      trackMMMEvent({
        component: CommonTrackedComponent.TAB_SELECTOR,
        action: "onSelect",
        data: getMMMEventSelectionData((args[0] as string) || "None"),
      });

      _setActiveTab(...args);
      document.querySelector("#app-scrollable-content-wrapper")?.scrollTo(0, 0);
    },
    [_setActiveTab, trackMMMEvent],
  );

  const isExploreMixesTab = useMemo(
    () => activeTab === ActiveTab.EXPLORE_MIXES,
    [activeTab],
  );
  const isModelInsightsTab = useMemo(
    () => activeTab === ActiveTab.MODEL_INSIGHTS,
    [activeTab],
  );
  const isPerformanceTrackingTab = useMemo(
    () => activeTab === ActiveTab.PERFORMANCE_TRACKING,
    [activeTab],
  );

  return {
    activeTab,
    setActiveTab,
    isExploreMixesTab,
    isModelInsightsTab,
    isPerformanceTrackingTab,
  } as const;
};

const ModelInsightsParam = CustomJsonParam<FixedDateInterval | string>();
export const useModelInsightsTimePeriod = () =>
  useQueryParam(
    "model-insights-time-period",
    withDefault(ModelInsightsParam, "all"),
    { updateType: "pushIn" },
  );

export const KPIParam = CustomJsonCompressedParam<
  ModelMetadataKpiFormula | undefined
>();
export const useModelInsightsKPI = () =>
  useQueryParam("model-insights-kpi", KPIParam, { updateType: "pushIn" });

export const useModelInsightsChartVisibleDatasets = () =>
  useQueryParam(
    "model-insights-chart-visible-datasets",
    withDefault(JsonParam, {
      [MODEL_FIT_ACTUAL_ID]: true,
      [MODEL_FIT_PREDICTION_ID]: true,
      [MODEL_FIT_PREDICTION_HDI_ID]: true,
      [MODEL_FIT_SEASONALITY_ID]: true,
      [MODEL_FIT_SPEND_ID]: true,
    }),
    { updateType: "pushIn" },
  );

export const useModelInsightsKpiTimePeriod = () => {
  const { is4WeekCycle } = useSelectedCycle();

  return useQueryParam(
    "model-insights-kpi-time-period",
    withDefault(StringParam, is4WeekCycle ? "last_4_weeks" : "last_26_weeks"),
    { updateType: "pushIn" },
  );
};

export type ChannelInsightsSortType =
  | "spend"
  | "kpi"
  | "efficiency"
  | "column_name";

export type ChannelInsightsSortDirection = "asc" | "desc";

export type ChannelInsightsSorting = {
  column: ChannelInsightsSortType;
  direction: ChannelInsightsSortDirection;
};

const ChannelInsightSortTypeParam =
  CustomJsonCompressedParam<ChannelInsightsSorting>();
export const useModelInsightsSorting = () => {
  return useQueryParam(
    "model-insights-sort",
    withDefault(ChannelInsightSortTypeParam, {
      column: "spend",
      direction: "desc",
    }),
    { updateType: "pushIn" },
  );
};

const StringArrayParam = CustomJsonCompressedParam<string[]>();
export const useModelInsightsKpiGeneralChartIds = () =>
  useQueryParam(
    "model-insights-chart-ids",
    withDefault(StringArrayParam, [] as string[]),
    { updateType: "pushIn" },
  );

export type GeneralChartSelectorType = "kpi" | "spend" | "roas";
const GeneralChartSelector =
  CustomJsonCompressedParam<GeneralChartSelectorType>();
export const useModelInsightsKpiGeneralChartSelector = () =>
  useQueryParam(
    "model-insights-chart-selector",
    withDefault(GeneralChartSelector, "kpi"),
    { updateType: "pushIn" },
  );

const getInitialChartMode = () => BudgetMixCardMode.CHART;

const getInitialBudgetMixTableExpandedRows = () => [];

export const useMMMBudgetMixChartMode = () =>
  useQueryParam(
    "mmm-bm-mode",
    withDefault(NumberParam, getInitialChartMode()),
    { updateType: "pushIn" },
  );

export const useMMMDateRange = () => {
  const [_dateRange, setDateRange] = useQueryParam(
    "mmm-date-range",
    CustomJsonCompressedParam<{ startDate: string; endDate: string }>(),
  );

  const dateRange = useMemo(() => _dateRange ?? {}, [_dateRange]);
  return [dateRange, setDateRange] as const;
};

// TODO: Fix this enum after globally enabling enableMmmUiUpdates
export enum HistoricalPeriodChoice {
  PREVIOUS_PERIOD,
  CUSTOM,
  SAME_PERIOD_LAST_YEAR,
}

export type HistoricalPeriodChoiceNew = 0 | 1 | 2 | string;

export const historicalPeriodChoiceToPretty = {
  [HistoricalPeriodChoice.CUSTOM]: "Custom period",
  [HistoricalPeriodChoice.PREVIOUS_PERIOD]: "Prior period",
  [HistoricalPeriodChoice.SAME_PERIOD_LAST_YEAR]: "Same time last year",
};
export const useMMMPeriodSelector = () => {
  return useQueryParam(
    "mmm-date-range-period-selector",
    withDefault(
      CustomJsonParam<HistoricalPeriodChoice>(),
      HistoricalPeriodChoice.PREVIOUS_PERIOD,
    ),
  );
};

export const useMMMTableExpandedRows = () => {
  return useQueryParam(
    "mmm-bm-rows",
    withDefault(
      CustomJsonCompressedParam<(number | string)[]>(),
      getInitialBudgetMixTableExpandedRows(),
    ),
  );
};

export const INHERIT_BUDGET_MIX_KPI = "-1";

const getInitialBudgetMixKpiId = () => INHERIT_BUDGET_MIX_KPI;

export const useMMMBudgetMixKpiId = () => {
  const [selectedBudgetMixKpiId, _setSelectedBudgetMixKpiId] =
    useQueryParam<string>(
      "bm-kpi",
      withDefault(StringParam, getInitialBudgetMixKpiId()),
    );

  const [selectedCacKpi, setSelectedCacKpi] = useCacDataState();
  const [selectedRoasKpi, setSelectedRoasKpi] = useRoasDataState();

  const setSelectedBudgetMixKpiId = (newValue: string) => {
    setSelectedCacKpi(newValue);
    setSelectedRoasKpi(newValue);
    _setSelectedBudgetMixKpiId(newValue);
  };

  const _isValidKpiId = (kpiId?: string) => {
    if (!kpiId) {
      return true;
    }

    const isNumeric = !isNaN(Number(kpiId));
    if (isNumeric) {
      return false;
    }

    return true;
  };

  const validateOrResetBudgetMixKpi = () => {
    const initialBudgetMixKpi = getInitialBudgetMixKpiId();
    if (!_isValidKpiId(selectedCacKpi)) {
      setSelectedCacKpi(initialBudgetMixKpi);
    }

    if (!_isValidKpiId(selectedRoasKpi)) {
      setSelectedRoasKpi(initialBudgetMixKpi);
    }
    if (!_isValidKpiId(selectedBudgetMixKpiId)) {
      _setSelectedBudgetMixKpiId(initialBudgetMixKpi);
    }
  };

  return {
    selectedBudgetMixKpiId,
    setSelectedBudgetMixKpiId,
    validateOrResetBudgetMixKpi,
  };
};

export const useExploreMixesTimePeriod = () =>
  useQueryParam(
    "explore-mixes-time-period",
    withDefault(StringParam, "last_26_weeks"),
    { updateType: "pushIn" },
  );

export const useClearBudgetMixUrlState = () => {
  const [, setExploreMixesTimePeriod] = useExploreMixesTimePeriod();
  const [, setBmMode] = useMMMBudgetMixChartMode();
  const [, setBmExpandedRows] = useMMMTableExpandedRows();
  const { setSelectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  const [, setHiddenBudgetMixes] = useHiddenBudgetMixes();
  const [, setCacDataState] = useCacDataState();
  const [, setRoasDataState] = useRoasDataState();
  const { setActiveTab } = useMMMActiveTab();
  const [, setAdoptedMix] = useAdoptedMix();
  const [, setNotifications] = useMMMNotifications();

  const clearState = () => {
    setExploreMixesTimePeriod("");

    setNotifications([]);
    setAdoptedMix(null);
    setBmMode(getInitialChartMode());

    setBmExpandedRows(getInitialBudgetMixTableExpandedRows());
    setSelectedBudgetMixKpiId(getInitialBudgetMixKpiId());
    setHiddenBudgetMixes(getInitialHiddenBudgetMixes());
    setCacDataState(getCacDataStateInitialValue());
    setRoasDataState(getRoasDataStateInitialValue());
    setActiveTab(ActiveTab.EXPLORE_MIXES);
  };

  return clearState;
};
